import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Link } from 'react-router-dom';

const AboutWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StorySection = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['3xl']};
  margin-bottom: ${theme.spacing['3xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const SectionContent = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.lg};
`;

const GruntworkSection = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['3xl']};
  margin-bottom: ${theme.spacing['3xl']};
  text-align: center;
`;

const GruntworkLogo = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.lg};
`;

const GruntworkTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const GruntworkDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto ${theme.spacing.lg};
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing['2xl']};
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ValueCard = styled.div`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  text-align: center;
  transition: transform ${theme.transitions.base};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ValueIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.md};
`;

const ValueTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
`;

const ValueDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const PhilosophySection = styled.div`
  margin: ${theme.spacing['3xl']} 0;
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${theme.spacing['3xl']};
  padding: ${theme.spacing['2xl']};
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const CTADescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xl};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
`;

export const AboutPage: React.FC = () => {
  return (
    <AboutWrapper>
      <Container>
        <Header>
          <Title>About Promptasaurus</Title>
          <Subtitle>
            We're on a mission to make AI conversations more effective, productive, and accessible to everyone.
          </Subtitle>
        </Header>

        <GruntworkSection>
          <GruntworkLogo>🔧</GruntworkLogo>
          <GruntworkTitle>A Gruntwork.ai Project</GruntworkTitle>
          <GruntworkDescription>
            Promptasaurus is proudly part of the Gruntwork.ai portfolio—a personal studio that develops 
            AI-first solutions to everyday problems as though they are startup products. Gruntwork.ai 
            believes in learning by doing and testing the power of AI not just to solve the problem at 
            hand, but to create production-ready, scalable, and last-mile complete solutions.
          </GruntworkDescription>
          <GruntworkDescription>
            This approach means Promptasaurus isn't just a proof of concept or a demo—it's built with 
            the same care, attention to detail, and commitment to user experience as any venture-backed 
            startup product. We're exploring what's possible when AI is used throughout the entire 
            product development lifecycle.
          </GruntworkDescription>
        </GruntworkSection>

        <StorySection>
          <SectionTitle>Our Story</SectionTitle>
          <SectionContent>
            Promptasaurus was born from a simple observation: even as AI models become more powerful, 
            many users struggle to communicate their needs effectively to these systems. We watched 
            countless people get frustrated with vague or unhelpful AI responses, not realizing that 
            the problem wasn't the AI—it was how they were asking.
          </SectionContent>
          <SectionContent>
            As part of the Gruntwork.ai initiative, we didn't just want to identify this problem—we 
            wanted to build a complete, polished solution. Using AI throughout our development process, 
            from ideation to implementation, we created Promptasaurus to be the bridge between human 
            intent and AI understanding.
          </SectionContent>
          <SectionContent>
            What started as a simple browser extension has evolved into a comprehensive suite of tools 
            designed to enhance every aspect of AI interaction. From our intelligent Fossil Fills 
            template system to our advanced prompt improvement engine, we're constantly innovating 
            to make AI more accessible and useful for everyone.
          </SectionContent>
        </StorySection>

        <PhilosophySection>
          <SectionTitle>Our Philosophy</SectionTitle>
          <ValueGrid>
            <ValueCard>
              <ValueIcon>🎯</ValueIcon>
              <ValueTitle>Simplicity</ValueTitle>
              <ValueDescription>
                We believe powerful tools should be easy to use. No complex setups, no API keys, 
                just instant improvement.
              </ValueDescription>
            </ValueCard>
            <ValueCard>
              <ValueIcon>🚀</ValueIcon>
              <ValueTitle>AI-First Development</ValueTitle>
              <ValueDescription>
                Built with AI, for AI interactions. We use the latest AI technologies not just in 
                our product, but throughout our development process.
              </ValueDescription>
            </ValueCard>
            <ValueCard>
              <ValueIcon>✨</ValueIcon>
              <ValueTitle>Last-Mile Polish</ValueTitle>
              <ValueDescription>
                As a Gruntwork.ai project, we go beyond MVPs. Every feature is refined, polished, 
                and ready for real-world use.
              </ValueDescription>
            </ValueCard>
          </ValueGrid>
        </PhilosophySection>

        <CTASection>
          <CTATitle>Join Us on Our Journey</CTATitle>
          <CTADescription>
            Experience what's possible when AI is used to build thoughtful, complete solutions to everyday problems.
          </CTADescription>
          <ButtonGroup>
            <Button as={Link} to="/get-started" size="large">
              Try Promptasaurus
            </Button>
            <Button 
              as="a" 
              href="https://gruntwork.ai" 
              target="_blank" 
              rel="noopener noreferrer" 
              variant="secondary" 
              size="large"
            >
              Explore Gruntwork.ai
            </Button>
          </ButtonGroup>
        </CTASection>
      </Container>
    </AboutWrapper>
  );
};